import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import SegmentMenu from "../navegation/SegmentMenu";
import { Fragment, useEffect, useState, useCallback, useMemo } from "react"; // Added useMemo
import { useDispatch, useSelector } from "react-redux"; // Added useDispatch
import { setSegments, clearSegments } from "../../store/redux_auth/authSlice"; // Added actions
import { fetchNoticationList } from "../../api/notifications";
import NotificationsTable from "./notificationsTable";
import LoadingPlaceholder from "../ui/loading/LoadingPlaceholder";
import { Handler401 } from "../../api/utils/responseActions";
import FilingNotificationService from "../../services/sockets/notifications/FilingNotificationService";
import LicenseValidationNotificationService from "../../services/sockets/notifications/LicenseValidationNotificationService";
import AffiliateNotificationService from "../../services/sockets/notifications/AffiliateNotificationService";
import { AFFILIATE_STATUS } from "../../utils/constants/affiliateConstants";
import { getUserRole, tokenLoader } from "../../utils/auth";
import { useRefreshOnAuth } from "../../hooks/useRefreshOnAuth";

function NotificationsEnabledAccountV2(props) {
  const dispatch = useDispatch(); // Initialize dispatch
  const token = props.token;
  const navigate = useNavigate();
  const location = useLocation();
  let [searchParams, setSearchParams] = useSearchParams();
  const notificationType = searchParams.get("ntype");
  const affiliateStatus = useSelector(
    (state) => state.auth.user?.affiliate_status,
  );

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- START TELEPORTATION LOGIC ---

  // 1. Memoize segments for referential stability
  const notificationSegments = useMemo(
    () => [
      {
        path: "/notifications",
        text: "All",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/notifications_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/notifications_active_blue.svg`,
      },
      {
        path: "/notifications?ntype=f",
        text: "Filings",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/doc_filing_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/doc_filing_blue.svg`,
      },
      {
        path: "/notifications?ntype=fi",
        text: "Finances",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/finance_graph_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/finance_graph_blue.svg`,
      },
    ],
    [],
  );

  // 2. Safety-checked effect to push segments to Redux without loops
  const segmentsString = JSON.stringify(notificationSegments);
  useEffect(() => {
    dispatch(setSegments(notificationSegments));

    return () => {
      dispatch(clearSegments());
    };
  }, [dispatch, segmentsString]);

  // --- END TELEPORTATION LOGIC ---

  const handleNotificationUpdate = (notificationId, updates) => {
    if (updates.deleted) {
      setNotifications((prevNotifications) =>
        prevNotifications.filter(
          (notification) => notification.id !== notificationId,
        ),
      );
    } else {
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          notification.id === notificationId
            ? { ...notification, ...updates }
            : notification,
        ),
      );
    }
  };

  const notificationList = useCallback(async () => {
    const apiUrl = "notifications/fetch-notifications";
    try {
      const response = await fetchNoticationList(apiUrl);
      if (!response.ok) {
        if (response.status === 401) {
          Handler401(navigate);
          return;
        }
        throw new Error("An issue while fetching notifications!");
      }
      const data = await response.json();
      setNotifications(data["notifications"]);
      setLoading(false);
    } catch (err) {
      console.log(err.message);
      setLoading(false);
    }
  }, [navigate]);

  useRefreshOnAuth(notificationList);

  useEffect(() => {
    const handleNotification = (message) => {
      setNotifications((prevNotifications) => [
        ...prevNotifications,
        message.message,
      ]);
    };

    const role = getUserRole();
    const socketPath =
      role === "CPA" ? "/ws/filing/provider" : "/ws/filing/taxpayer";

    const socketService = new FilingNotificationService(socketPath);
    const subscription = socketService
      .getMessages()
      .subscribe(handleNotification);

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const role = getUserRole();
    if (role === "CPA") {
      const socketService = new LicenseValidationNotificationService();
      const subscription = socketService.getMessages().subscribe((message) => {
        if (message.type === "tax_expert_license_validation") {
          notificationList();
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [notificationList]);

  useEffect(() => {
    if (
      affiliateStatus === AFFILIATE_STATUS.REV ||
      affiliateStatus === AFFILIATE_STATUS.REJ
    ) {
      const socketService = new AffiliateNotificationService();
      const subscription = socketService.getMessages().subscribe((message) => {
        if (message.type === "affiliate_notification" && message.message) {
          setNotifications((prevNotifications) => [
            message.message,
            ...prevNotifications,
          ]);
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [affiliateStatus]);

  useEffect(() => {
    notificationList();
  }, [token, notificationList]);

  return (
    <div className="main-content-area">
      <div className="content">
        <div className="ich-wrapper">
          <div className="internal-content-header">
            <SegmentMenu
              segments={notificationSegments}
              currentPath={location.pathname + location.search}
              loading={loading}
            />
          </div>
        </div>
        {loading ? (
          <div className="internal-content-body">
            <LoadingPlaceholder rows={5} type="card" />
          </div>
        ) : (
          <div className="internal-content-body">
            <NotificationsTable
              notifications={notifications}
              ntype={notificationType}
              onNotificationUpdate={handleNotificationUpdate}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationsEnabledAccountV2;
